import request, {Response, SuperAgentRequest} from 'superagent';
import {getAddress, getToken} from "./utils/common";
import {
    ChangePatientDoctorsRequest,
    Dept,
    Doctor,
    Patient,
    Appointment,
    Observer,
    Institution
} from "./components/dashboard";
import {EventJournalRecord, PatientEvent} from "./components/workTablePatients/EditorTabs/CureProgramTab";
import history from "./history"
// SivovaAlex-20240517-ActivitySettingsRollback
// import {ActivitySettingsConfigModel, ConfigPanelOptions} from "./components/configPanel";
import {ConfigPanelOptions, UsefulInformationPanelOptions} from "./components/configPanel";
import {Test} from "./components/test/TestModel";
import {TestResult, TestResultListOptions} from "./components/test/TestResultModel";
import {PatientMassiveEvent} from "./components/workTablePatients/EditorTabs/MassiveEventModal";
import {TaskModel, TaskResult, TaskStatus, TaskTypeModel} from "./components/workTableTask/TaskModel";
import {
    ActionLogCreateRequest,
    ActionLogListRequest,
} from "./components/workTableActionLogs/ActionLogModel";

export type RequestMethod = "get" | "post" | "put" | "delete" | "patch";

export const sendRequest = (method: RequestMethod, endpoint: string, data: object): SuperAgentRequest => {
    return request[method](getAddress() + endpoint)
        .set('x-access-token', getToken())
        .send(data);
};

export const sendMultipartRequest = (
    method: "post" | "put" | "patch",
    endpoint: string,
    fileFieldName: string,
    file: File,
    fields?: Record<string, string | number | boolean | null | undefined>
): SuperAgentRequest => {
    const requestBody = request[method](getAddress() + endpoint)
        .set('x-access-token', getToken())
        .attach(fileFieldName, file);

    if (fields) {
        Object.entries(fields).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }
            requestBody.field(key, String(value));
        });
    }

    return requestBody;
};

const getFileLink = (fileName: string, uploadDate: Date): string => {
    return `/userFile/get/?fileName=${fileName}&uploadDate=${uploadDate}`
};

const getFileLinkById = (fileId: string): string => {
    return `/userFile/get/?id=${fileId}`
};

export const handleResponseErrors = (error: any): any => {
    let message: string | undefined = error.response?.body?.message ?? error.message;
    if (error.message === 'Unauthorized') {
        alert(message);
        history.push('/');
    } else {
        alert(message);
    }
    return error?.response?.body;
};

export const getResponseBody = (response: Response) => {
    return response.body;
};

export interface PaginateOptions {
    search?: string,
    page: number,
    limit: number,
    sort?: string,
    doctorId?: string,
    dateStart?: Date,
    dateEnd?: Date,
    isViewed?: boolean,
    type?: string,
    deptId?: string,
    specialGroupsIdsFilter?: string | string[],
    patientDeptsIdsFilter?: string | string[],
    activatedFilter?: boolean,
    activatedDateFromFilter?: Date,
    activatedDateToFilter?: Date,
    registerDateFromFilter?: Date,
    registerDateToFilter?: Date,
    diagnosisFilter?: string,
    telFilter?: string,
    isLargeFamilyFilter?: boolean,
    filterEventType?: string,
    filterMassiveEventType?: string,
    filterMassiveEventStatus?: string,
}

export type FilterOptions = Omit<PaginateOptions, 'page' | 'limit' | 'sort'>;

export interface ResponsePaginateOptions {
    page: number,
    limit: number,
    totalDocs: number,
    offset: number,
    totalPages: number,
    pagingCounter: number,
    hasPrevPage: boolean,
    hasNextPage: boolean,
    prevPage?: number,
    nextPage?: number;
}

interface ResponseError {
    error: boolean,
    message: string,
}

const emptyPaginateOptions: PaginateOptions = {
    limit: 9999,
    page: 1,
    search: '',
};

const DoctorProvider = {
    create: (doc: Doctor) => sendRequest('post', '/doctor/create', doc)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (doc: Doctor) => sendRequest('post', '/doctor/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: PaginateOptions) => sendRequest('post', '/doctor/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/doctor/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (docID: string) => sendRequest('post', '/doctor/remove', {id: docID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
    exportDoctorList: (doctorId?: string): Promise<TaskModel> => sendRequest('get', doctorId ? `/doctor/exportDoctorList?doctorId=${doctorId}` : `/doctor/exportDoctorList`, {})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
};

const ObserverProvider = {
    create: (doc: Observer) => sendRequest('post', '/observer/create', doc)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (doc: Observer) => sendRequest('post', '/observer/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: PaginateOptions) => sendRequest('post', '/observer/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/observer/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (docID: string) => sendRequest('post', '/observer/remove', {id: docID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const InstitutionProvider = {
    edit: (doc: Institution) => sendRequest('post', '/institution/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: PaginateOptions) => sendRequest('post', '/institution/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/institution/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const DeptProvider = {
    create: (doc: Dept) => sendRequest('post', '/dept/create', doc)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (doc: Dept) => sendRequest('post', '/dept/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: PaginateOptions) => sendRequest('post', '/dept/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/dept/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (docID: string) => sendRequest('post', '/dept/remove', {id: docID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const UserFileTypeProvider = {
    create: (doc: Dept) => sendRequest('post', '/userFileType/create', doc)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (doc: Dept) => sendRequest('post', '/userFileType/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: PaginateOptions) => sendRequest('post', '/userFileType/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/userFileType/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (docID: string) => sendRequest('post', '/userFileType/remove', {id: docID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const SpecialGroupProvider = {
    getList: (options?: PaginateOptions) => sendRequest('post', '/specialGroup/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const AppointmentProvider = {
    create: (doc: Appointment) => sendRequest('post', '/appointment/create', doc)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (doc: Appointment) => sendRequest('post', '/appointment/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: PaginateOptions) => sendRequest('post', '/appointment/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/appointment/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (docID: string) => sendRequest('post', '/appointment/remove', {id: docID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const PatientProvider = {
    create: (patient: Patient) => sendRequest('post', '/patient/create', patient)
        .then((response) => response.body)
        .catch((err) => handleResponseErrors(err)),
    edit: (patient: Patient) => sendRequest('post', '/patient/edit', patient)
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    changePatientsDoctor: (request: ChangePatientDoctorsRequest) =>
        sendRequest('post', '/patient/changePatientsDoctor', request)
            .then(getResponseBody)
            .catch((err) => handleResponseErrors(err)),
    getList: (options: PaginateOptions) => sendRequest('post', '/patient/getList', options)
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    getActivatedCount: (options: FilterOptions) =>
        sendRequest('post', '/patient/getActivatedCount', options)
            .then(getResponseBody)
            .catch((err) => handleResponseErrors(err)),
    open: (patientID: string) => sendRequest('post', '/patient/getByID', {id: patientID})
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    remove: (patientID: string) => sendRequest('post', '/patient/remove', {id: patientID})
        .catch((err) => handleResponseErrors(err)),
    removeMany: (patientIds: string[]) => sendRequest('post', '/patient/removeMany', { patientIds })
        .catch((err) => handleResponseErrors(err)),
    refreshCodeExpiration: (id: string) => sendRequest('post', '/patient/refreshCode', {id: id})
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    sendReactivationMessage: (id: string) => sendRequest('post', '/patient/sendReactivationMessage', { id: id })
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    cancelActivation: (id: string) => sendRequest('post', '/patient/cancelActivation', { id: id })
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    massTransferToMax: (patientIds: string[]) => sendRequest('post', '/patient/massTransferToMax', { patientIds: patientIds })
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    getInteractionHistory: (patientId: string) => sendRequest('post', '/patient/interactionHistory/getList', {patientId: patientId})
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    editInteractionHistory: (id: string, comment: string, status: string) => sendRequest('post', '/patient/interactionHistory/edit', {
        id: id,
        comment: comment,
        status: status
    })
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    createInteractionHistory: (patientId: string) => sendRequest('post', '/patient/interactionHistory/create', {patientId: patientId})
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    getDeviationsByPatientId: (patientId: string) => sendRequest('post', '/patient/deviations/getByPatientId', {id: patientId})
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    getDeviationsList: () => sendRequest('post', '/patient/deviations/getList', {})
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    exportPatientList: (doctorId?: string): Promise<TaskModel> => sendRequest('get', doctorId ? `/patient/export?doctorId=${doctorId}` : `/patient/export`, {})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
    exportCallHistory: (doctorId?: string): Promise<TaskModel> => sendRequest('get', doctorId ? `/patient/exportCallHistory?doctorId=${doctorId}` : `/patient/exportCallHistory`, {})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
    exportMassiveEvents: (startDate?: string, endDate?: string): Promise<TaskModel> => sendRequest('get',
            startDate && endDate ?
                `/patient/exportMassiveEvent?startDate=${startDate}&endDate=${endDate}` :
                `/patient/exportMassiveEvent`, {})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
    exportPatientAnswers: (startDate?: string, endDate?: string): Promise<TaskModel> => sendRequest('get',
        startDate && endDate ?
            `/patient/exportPatientsAnswer?startDate=${startDate}&endDate=${endDate}` :
            `/patient/exportPatientsAnswer`, {})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
    exportVeteranPatientList: (doctorId?: string): Promise<TaskModel> => sendRequest('get', doctorId ? `/patient/exportVeteran?doctorId=${doctorId}` : `/patient/exportVeteran`, {})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
    exportPatientCards: (patientIds: string[]): Promise<TaskModel> => sendRequest('post', '/patient/exportCard', { patientIds })
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
};

const FileProvider = {
    getList: (id: string) => sendRequest('post', '/userFile/getList', {id: id})
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err)),
    getFile: (name: string, uploadDate: Date) => sendRequest('get', `${getFileLink(name, uploadDate)}`, {})
        .responseType('arraybuffer')
        .then((res: Response) => {
            return res;
        })
        .catch((err) => handleResponseErrors(err)),
    getFileById: (id: string) => sendRequest('get', `${getFileLinkById(id)}`, {})
        .responseType('arraybuffer')
        .then((res: Response) => {
            return res;
        })
        .catch((err) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/userFile/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    setType: (id: string, typeId: string) => sendRequest('put',
        '/userFile/setType/' + id.toString() + "?type=" + typeId, {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    parse: (id: string) => sendRequest('get',
        '/userFile/parse/' + id.toString(), {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    upload: (file: File, userFileType: string, patientId: string) =>
        sendMultipartRequest('post', "/userFile/upload", "file", file, {userFileType, patientId})
            .responseType('arraybuffer')
            .then((res: Response) => {
                return res;
            })
            .catch((err) => handleResponseErrors(err)),
    uploadEventFile: (file: File) =>
        sendMultipartRequest('post', "/userFile/uploadEventFile", "file", file)
            .then(res => res)
            .catch((err) => handleResponseErrors(err)),
    remove: (id: string) => sendRequest('delete',
        '/userFile/' + id.toString(), {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),

};

const AuthProvider = {
    login: (login: string, password: string) => sendRequest('post', '/auth/login', {
        login: login,
        password: password
    }),
    logout: () => sendRequest('post', '/auth/logout', {})
            .then(getResponseBody)
            .catch((err) => handleResponseErrors(err)),
    telegramLogin: (data: TelegramAuthResponse) => sendRequest('post', '/auth/telegramLogin', data),
    changeAdminPassword: (oldPassword: string, newPassword: string) =>
        sendRequest('post', '/auth/changeAdminPassword', {
            oldPassword: oldPassword,
            newPassword: newPassword
        }).catch((err: ResponseError) => handleResponseErrors(err)),
};

const PatientEventProvider = {
    getList: (patientID: string, eventType: string | undefined) => sendRequest('post',
        '/patientEvent/getList' + (eventType !== undefined ? "?eventType=" + eventType : ""),
        {id: patientID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    create: (patientEvent: {
        repeatDays: string[]; patientId: string; endDate: Date; repeatType: string; description: string;
        eventType: string; cancellationTimeframeTo: Date | null; finishType: string;
        repeatByWeeksCount: number;
        repeatByMonthsCount: number;
        repeatByDaysCount: number; journal: EventJournalRecord[]; repeatSeveralTimesCount: number; _id: string; cancellationTimeframeFrom: Date | null; startDate: Date | undefined; fileIds?: string[]
    }) => sendRequest('post', '/patientEvent/create', patientEvent)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (patientID: string) => sendRequest('post', '/patientEvent/remove', {id: patientID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (patientEvent: PatientEvent) => sendRequest('post', '/patientEvent/edit', patientEvent)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (patientEventID: string) => sendRequest('post', '/patientEvent/getByID', {id: patientEventID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getHospitalizations: (deptId?: String, doctorId?: String) =>
        sendRequest('post',
            '/patientEvent/hospitalizations?t=1' +
            (deptId ? ("&deptId=" + deptId) : "") +
            (doctorId ? ("&doctorId=" + doctorId) : "")
            , {})
            .then(getResponseBody)
            .catch((err: ResponseError) => handleResponseErrors(err)),
};

const PatientMassiveEventProvider = {
    getList: (options?: PaginateOptions) => sendRequest('post', '/patientMassiveEvent/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    create: (patientMassiveEvent: {
        repeatDays: string[]; patientIds: string[]; type: string; endDate: Date; repeatType: string; description: string; specialgroupids: string[]; specialGroupModeAll: boolean, doctorids: string[]; patientDeptIds: string[];
        eventType: string; cancellationTimeframeTo: Date | null; finishType: string;
        repeatByWeeksCount: number;
        repeatByMonthsCount: number;
        repeatByDaysCount: number; journal: EventJournalRecord[]; repeatSeveralTimesCount: number; _id: string; cancellationTimeframeFrom: Date | null; startDate: Date | undefined; fileIds?: string[]
    }) => sendRequest('post', '/patientMassiveEvent/create', patientMassiveEvent)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (patientMassiveEventID: string) => sendRequest('post', '/patientMassiveEvent/remove', {id: patientMassiveEventID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
    // edit: (patientMassiveEventID: { repeatByMonthsCount: number; repeatDays: string[]; endDate: Date; repeatType: string; description: string; eventType: string; finishType: string; cancellationTimeframeTo: Date | null; patientIds: string[]; type:string; repeatByWeeksCount: number; repeatByDaysCount: number; journal: EventJournalRecord[]; repeatSeveralTimesCount: number; _id: string; cancellationTimeframeFrom: Date | null; startDate: Date }) => sendRequest('post', '/patientMassiveEvent/edit', patientMassiveEventID)
    //     .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (patientMassiveEventID: string) => sendRequest('post', '/patientMassiveEvent/getByID', {id: patientMassiveEventID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    // getHospitalizations: (deptId?: String, doctorId?: String) =>
    //     sendRequest('post',
    //         '/patientMassiveEvent/hospitalizations?t=1' +
    //         (deptId ? ("&deptId=" + deptId) : "") +
    //         (doctorId ? ("&doctorId=" + doctorId) : "")
    //         , {})
    //         .then(getResponseBody)
    //         .catch((err: ResponseError) => handleResponseErrors(err)),
};

const PatientActivityProvider = {
    getList: (options: PaginateOptions) => sendRequest('post', '/patientActivity/getList', options)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    markAsViewed: (id: string, isViewed: boolean) => sendRequest('post',
        (isViewed ?
            '/patientActivity/view/' : '/patientActivity/unview/') + id.toString(), {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err))
};

const ConfigProvider = {
    get: () => sendRequest('post', '/config/get', {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (config: ConfigPanelOptions) => sendRequest('post', '/config/edit', config)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    // SivovaAlex-20240517-ActivitySettingsRollback
    // editActivitySettings: (activitySettings: ActivitySettingsConfigModel) => sendRequest('post', '/config/editActivitySettings', activitySettings)
    //     .catch((err: ResponseError) => handleResponseErrors(err)),
    checkTransport: () => sendRequest('post', '/config/checkTransport', {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    messengers: () => sendRequest('get', '/config/messengers', {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const BackupProvider = {
    status: () => sendRequest('get', '/backup/status', {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    execute: () => sendRequest('post', '/backup/execute', {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    list: () => sendRequest('get', '/backup/list', {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    log: (id: string) => sendRequest('get', '/backup/log/' + id, {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const UsefulInformationProvider = {
    get: () => sendRequest('post', '/usefulInformation/get', {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (config: UsefulInformationPanelOptions) => sendRequest('post', '/usefulInformation/edit', config)
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const TestProvider = {
    create: (doc: Test) => sendRequest('post', '/test/create', doc)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (doc: Test) => sendRequest('post', '/test/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: PaginateOptions) => sendRequest('post', '/test/getList', options ? options : emptyPaginateOptions)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/test/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (docID: string) => sendRequest('post', '/test/remove', {id: docID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

const TestResultProvider = {
    create: (doc: TestResult) => sendRequest('post', '/testResult/create', doc)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    edit: (doc: TestResult) => sendRequest('post', '/testResult/edit', doc)
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    getList: (options?: TestResultListOptions) => sendRequest('post', '/testResult/getList', options ? options : {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    open: (docID: string) => sendRequest('post', '/testResult/getByID', {id: docID})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    remove: (docID: string) => sendRequest('post', '/testResult/remove', {id: docID})
        .catch((err: ResponseError) => handleResponseErrors(err)),
};

export interface TaskListRequest {
    page: number,
    limit: number,
    doctorId?: string,
    taskTypeId?: string,
    result?: TaskResult,
    status?: TaskStatus
}

export interface TaskListResponse extends ResponsePaginateOptions {
    docs: TaskModel[]
}

export type TaskTypeListResponse = TaskTypeModel[];

const TaskProvider = {
    list: (options: TaskListRequest): Promise<TaskListResponse> => sendRequest('post', '/task/getList', options)
        .then<TaskListResponse>((res) => Object.assign(<TaskListResponse>{}, res.body))
        .catch((err: ResponseError) => handleResponseErrors(err)),

    open: (id: String): Promise<TaskModel> => sendRequest('post', '/task/getByID', {id: id})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err: ResponseError) => handleResponseErrors(err)),

    typeList: (): Promise<TaskTypeListResponse> => sendRequest('post', '/task/getTypeList', {})
        .then<TaskTypeListResponse>((res) => res.body as TaskTypeListResponse)
        .catch((err: ResponseError) => handleResponseErrors(err)),
};



const ActionLogProvider = {
    getList: (options?: ActionLogListRequest) => sendRequest('post', '/actionLog/getList', options ? options : {})
        .then(getResponseBody)
        .catch((err: ResponseError) => handleResponseErrors(err)),
    exportActionLogList: (): Promise<TaskModel> => sendRequest('get', `/actionLog/exportActionLogList`, {})
        .then<TaskModel>((res) => Object.assign(<TaskModel>{}, res.body))
        .catch((err) => handleResponseErrors(err)),
    create: (logData: ActionLogCreateRequest) => sendRequest('post', '/actionLog/create', logData)
        .then(getResponseBody)
        .catch((err) => handleResponseErrors(err))
};

export default {
    DoctorProvider,
    ObserverProvider,
    InstitutionProvider,
    PatientProvider,
    AuthProvider,
    FileProvider,
    PatientEventProvider,
    PatientMassiveEventProvider,
    ConfigProvider,
    UsefulInformationProvider,
    PatientActivityProvider,
    DeptProvider,
    TestProvider,
    TestResultProvider,
    UserFileTypeProvider,
    SpecialGroupProvider,
    AppointmentProvider,
    BackupProvider,
    TaskProvider,
    ActionLogProvider
};
